/* `/tasks` — scheduled tasks: a prompt an agent runs on a timer, and the runs
   it started. `backend/modules/tasks/README.md` for the wire.

   In `shared/api/` because the app shell lays tasks over its agents, the
   automations pages run them, and `events.ts` refreshes them when a run starts
   or ends — and `shared/` may not import a feature (rule book §1 rule 5).
   Turning a row into the pages' `AgentTask` is the automations feature's
   (`lib/serverTasks.ts`): it needs the feature's schedule helpers.

   Paths are bare; `client.baseURL` carries `/api/v1`. Every response goes
   through `unwrap`. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";
import { UNFINISHED } from "./threads";

import type { TaskCreate, TaskPatch, TaskRow, TaskRunRow, TaskRunStarted } from "@/shared/types";

export const taskKeys = {
  all: ["tasks"] as const,
  list: () => [...taskKeys.all, "list"] as const,
  runs: (taskId: string) => [...taskKeys.all, "runs", taskId] as const,
};

/** How often a list with a run in flight is re-read, in case the socket that
    says it ended is down: the Run button must not spin forever. */
const RUNNING_REFRESH_MS = 5_000;

/* ------------------------------ endpoints -------------------------------- */

/** Every task in the workspace, oldest first. */
export async function listTasks(): Promise<TaskRow[]> {
  return unwrap(await client.get<Envelope<TaskRow[]>>("/tasks"));
}

export async function createTask(body: TaskCreate): Promise<TaskRow> {
  return unwrap(await client.post<Envelope<TaskRow>>("/tasks", body));
}

export async function patchTask(id: string, body: TaskPatch): Promise<TaskRow> {
  return unwrap(await client.patch<Envelope<TaskRow>>(`/tasks/${id}`, body));
}

export async function deleteTask(id: string): Promise<void> {
  await client.delete(`/tasks/${id}`);
}

/** Runs it now, in a new chat. 409 while its last run is still going. */
export async function runTask(id: string): Promise<TaskRunStarted> {
  return unwrap(await client.post<Envelope<TaskRunStarted>>(`/tasks/${id}/run`));
}

/** Newest first. */
export async function listTaskRuns(id: string): Promise<TaskRunRow[]> {
  return unwrap(await client.get<Envelope<TaskRunRow[]>>(`/tasks/${id}/runs`));
}

/* -------------------------------- hooks ---------------------------------- */

export const useTasks = (enabled = true) =>
  useQuery({
    queryKey: taskKeys.list(),
    queryFn: listTasks,
    enabled,
    refetchInterval: (query) =>
      query.state.data?.some((t) => t.last_run && UNFINISHED.includes(t.last_run.status))
        ? RUNNING_REFRESH_MS
        : false,
  });

export const useTaskRuns = (id: string | null) =>
  useQuery({ queryKey: taskKeys.runs(id ?? ""), queryFn: () => listTaskRuns(id as string), enabled: !!id });

/** Writes refresh every task query: a run list or the calendar may show the
    same task. Failures reach the global toast (queryClient.ts). */
function useTaskWrite<A, R>(mutationFn: (args: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn, onSuccess: () => qc.invalidateQueries({ queryKey: taskKeys.all }) });
}

export const useCreateTask = () => useTaskWrite(createTask);
export const usePatchTask = () => useTaskWrite(({ id, body }: { id: string; body: TaskPatch }) => patchTask(id, body));
export const useDeleteTask = () => useTaskWrite(deleteTask);

/** The list shows the run at once, before the refetch lands, so the button
    goes straight from Run to Running. */
export function useRunTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: runTask,
    onSuccess: (run, id) => {
      qc.setQueryData<TaskRow[]>(taskKeys.list(), (rows) =>
        rows?.map((t) =>
          t.id === id
            ? {
                ...t,
                last_run_at: run.started_at,
                last_run: { id: run.id, status: run.status, thread_id: run.thread_id, started_at: run.started_at, ended_at: null, error: null },
              }
            : t,
        ),
      );
      return qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}
